import { format } from 'date-fns'
import { useIntl, defineMessages } from 'react-intl'
import trendDown from '../../../../assets/homes/img/trendDown.svg'
import { Listing, ListingLoanInfo } from '../../../../api/gqlaxy/gql/generated/graphql'
import {
  Card,
  Heading,
  CardBody,
  CardHeader,
  Flex,
  Icon,
  Image,
  Text,
  Link,
  Button
} from '@homebotapp/hb-react-component-catalog'
import { formatCurrency } from '../../../../helpers/formatters'
import { AssumablesLearnMoreModal } from '../../AssumablesLearnMoreModal/AssumablesLearnMoreModal'
import { Waypoint } from 'react-waypoint'
import { useTrackingDebounceSetters } from '../../../../hooks/tracking'
import { useTrackingContext } from '../../../../providers/tracking'
import { LISTINGS_DETAILED, HOME_HIGHLIGHTS } from '../../../../constants/tracking/trackingEventConstants'
import { selectCustomerProfile } from '../../../../store/selectors/customerProfile'
import { useSelector } from 'react-redux'
import { formatAbbrCurrency } from '../../../../helpers/formatters'

export const MSG = defineMessages({
  head: {
    id: 'HomeSearch.listingDetails.Highlights.heading',
    defaultMessage: 'Home Highlights'
  },
  priceDropHead: {
    id: 'HomeSearch.listingDetails.Highlights.priceDropHead',
    defaultMessage: 'Price drop'
  },
  priceDrop: {
    id: 'HomeSearch.listingDetails.Highlights.priceDrop',
    defaultMessage: 'They dropped it by {priceChange} on {priceChangeDate}'
  },
  concessionsHead: {
    id: 'HomeSearch.listingDetails.Highlights.concessionsHead',
    defaultMessage: 'Concessions'
  },
  concessions: {
    id: 'HomeSearch.listingDetails.Highlights.concessions',
    defaultMessage: 'Check the home description for details.'
  },
  assumableHead: {
    id: 'HomeSearch.listingDetails.Highlights.assumableHead',
    defaultMessage: 'Assumable mortgage'
  },
  assumableInfo: {
    id: 'HomeSearch.listingDetails.Highlights.assumableInfo',
    defaultMessage: '{type} Loan · {loanAmount} balance · {termYears} years remaining · '
  },
  newListingHead: {
    id: 'HomeSearch.listingDetails.Highlights.newListingHead',
    defaultMessage: 'New listing!'
  },
  newListingInfo: {
    id: 'HomeSearch.listingDetails.Highlights.newListingInfo',
    defaultMessage: 'It went on the market on {onMarketDate}'
  },
  info: {
    id: 'HomeSearch.listingDetails.Highlights.info',
    defaultMessage: 'Info'
  },
  openHouseHead: {
    id: 'HomeSearch.listingDetails.Highlights.openHouseHead',
    defaultMessage: 'Open House'
  },
  openHouse: {
    id: 'HomeSearch.listingDetails.Highlights.openHouse',
    defaultMessage: '{date} at {time}'
  },
  concessionsDisclaimer: {
    id: 'HomeSearch.listingDetails.Highlights.concessionsDisclaimer',
    defaultMessage: '✨ AI-generated based on the'
  },
  concessionsDisclaimerStyled: {
    id: 'HomeSearch.listingDetails.Highlights.concessionsDisclaimerStyled',
    defaultMessage: 'property description'
  },
  possibleSavings: {
    id: 'HomeSearch.listingDetails.Highlights.possibleSavings',
    defaultMessage: 'Possible savings'
  },
  seeDetails: {
    id: 'HomeSearch.listingDetails.Highlights.seeDetails',
    defaultMessage: 'See details'
  },
  likelyNoSavings: {
    id: 'HomeSearch.listingDetails.Highlights.likelyNoSavings',
    defaultMessage: 'Likely no savings for you'
  },
  perMonth: {
    id: 'HomeSearch.listingDetails.Highlights.perMonth',
    defaultMessage: '{amount}/mo'
  }
})

export interface HomeHighlightsProps {
  assumableMortgageLoanInfo?: ListingLoanInfo | null
  assumablePotentialSavings?: number | null
  estimatedMonthlyPaymentRef?: React.RefObject<HTMLDivElement>
  listing?: Listing
  propertyDescriptionRef?: React.RefObject<HTMLDivElement>
  setAssumableToggle: (toggle: boolean) => void
}

export const HomeHighlights = ({
  assumableMortgageLoanInfo,
  assumablePotentialSavings,
  estimatedMonthlyPaymentRef,
  listing,
  propertyDescriptionRef,
  setAssumableToggle
}: HomeHighlightsProps) => {
  const intl = useIntl()
  const cp = useSelector(selectCustomerProfile)
  const { trackingClient } = useTrackingContext()
  const priceChange = listing?.priceCents ? listing?.priceCents - listing?.previousListPriceCents : 0
  const priceChangeDate = listing?.priceChangeTimestamp ? format(new Date(listing.priceChangeTimestamp), 'MMM d') : ''
  const futureOpenHouses = listing?.openHouses?.filter(openHouse => new Date(openHouse.endTime) > new Date())
  const startTime = new Date(futureOpenHouses?.[0]?.startTime)

  const marketDateObject = new Date(listing?.onMarketDate)
  const thirtyDaysAgo = new Date()
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
  // Check if onMarketDate is within the last 30 days
  const isWithinLast30Days = marketDateObject >= thirtyDaysAgo
  const [trackModuleViewed] = useTrackingDebounceSetters(
    () =>
      trackingClient?.moduleViewed({
        event_source: LISTINGS_DETAILED,
        module_name: HOME_HIGHLIGHTS
      })
  )

  if (
    !listing?.isAssumable &&
    !listing?.hasSellerConcession &&
    priceChange >= 0 &&
    !futureOpenHouses?.length &&
    !isWithinLast30Days
  ) {
    return null
  }

  const handleAssumableDetailsClick = () => {
    // set assumable toggle to true
    setAssumableToggle?.(true)
    // smooth scroll to breakdown section
    estimatedMonthlyPaymentRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }

  return (
    <Card size='lg' my={8}>
      <CardHeader
        p={4}
        gap={4}
        display='flex'
        alignItems='center'
        flexDirection='row'
        borderBottom='1px solid var(--brand-primary-border-50)'
      >
        <Icon name='solar' h={6} w={6} />
        <Heading as='h3' textAlign='left' mb={0}>
          {intl.formatMessage(MSG.head)}
        </Heading>
      </CardHeader>
      <CardBody px={4} pt={0} pb={4}>
        <Waypoint onEnter={() => trackModuleViewed()} bottomOffset='25%' />
        {futureOpenHouses?.length > 0 && (
          <Flex align='center' gap={4} mt={4}>
            <Flex justify='center' align='center' bg='success.200' borderRadius='50%' h={10} w={10}>
              {/* @ts-ignore */}
              <Icon h={6} w={6} name='opendoor' color='#30A46C' />
            </Flex>
            <Flex direction='column'>
              <Heading as='h4' variant='eyebrow' size='xs' mb={0}>
                {intl.formatMessage(MSG.openHouseHead)}
              </Heading>
              {startTime && (
                <Text size='xs' mb={0}>
                  {intl.formatMessage(MSG.openHouse, {
                    date: format(startTime, 'E M/d'),
                    time: format(startTime, 'h:mm a')
                  })}
                </Text>
              )}
            </Flex>
          </Flex>
        )}
        {isWithinLast30Days && (
          <Flex align='center' gap={4} mt={4}>
            <Flex justify='center' align='center' bg='success.200' borderRadius='50%' h={10} w={10}>
              {/* @ts-ignore */}
              <Icon h={6} w={6} name='star' color='#30A46C' />
            </Flex>
            <Flex direction='column'>
              <Heading as='h4' variant='eyebrow' size='xs' mb={0}>
                {intl.formatMessage(MSG.newListingHead)}
              </Heading>
              <Text size='xs' mb={0}>
                {intl.formatMessage(MSG.newListingInfo, {
                  onMarketDate: format(new Date(listing?.onMarketDate), 'MMM d')
                })}
              </Text>
            </Flex>
          </Flex>
        )}
        {priceChange < 0 && (
          <Flex align='center' gap={4} mt={4}>
            <Flex justify='center' align='center' bg='success.200' borderRadius='50%' h={10} w={10}>
              <Image h={6} w={6} src={trendDown} alt='trend' />
            </Flex>
            <Flex direction='column'>
              <Heading as='h4' variant='eyebrow' size='xs' mb={0}>
                {intl.formatMessage(MSG.priceDropHead)}
              </Heading>
              <Text size='xs' mb={0}>
                {intl.formatMessage(MSG.priceDrop, {
                  priceChange: <b>{formatAbbrCurrency(Math.abs(priceChange) / 100)}</b>,
                  priceChangeDate
                })}
              </Text>
            </Flex>
          </Flex>
        )}
        {listing?.hasSellerConcession && (
          <Flex align='center' gap={4} mt={4}>
            <Flex
              justify='center'
              align='center'
              bg='success.200'
              borderRadius='50%'
              h={10}
              w={10}
              flexShrink={0}
              overflow='hidden'
            >
              {/* @ts-ignore */}
              <Icon h={6} w={6} name='sellpocket' color='#30A46C' />
            </Flex>
            <Flex direction='column'>
              <Heading as='h4' variant='eyebrow' size='xs' mb={0}>
                {intl.formatMessage(MSG.concessionsHead)}
              </Heading>
              <Text size='xs' mb={0}>
                {listing?.concessionSummary ? listing?.concessionSummary : intl.formatMessage(MSG.concessions)}
              </Text>
              {listing?.concessionSummary && (
                <Text size='3xs' mb={0} color='neutral-600'>
                  {intl.formatMessage(MSG.concessionsDisclaimer)}
                  <Link
                    onClick={() => {
                      propertyDescriptionRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
                    }}
                    tracking={{
                      guid: '1qySNyNFfuj99BNJeKWM3U',
                      ui_context: 'Home Highlights - Listing Detail',
                      descriptor: 'clicked to see description for more details on concessions'
                    }}
                    href={'#property-description'}
                  >
                    <Text as='span' size='3xs' color='primary.800' fontWeight='bold' ml={1.5}>
                      {intl.formatMessage(MSG.concessionsDisclaimerStyled)}
                    </Text>
                  </Link>
                </Text>
              )}
            </Flex>
          </Flex>
        )}
        {cp?.assumableMortgagesEnabled && listing?.isAssumable && listing?.assumableLoanType && (
          <>
            <Flex align='center' gap={4} mt={4}>
              <Flex justify='center' align='center' bg='success.200' borderRadius='50%' h={10} w={10}>
                {/* @ts-ignore */}
                <Icon h={6} w={6} name='refi' color='#30A46C' />
              </Flex>
              <Flex direction='column'>
                <Flex direction='row' gap={1}>
                  <Heading as='h4' variant='eyebrow' size='xs' mb={0}>
                    {intl.formatMessage(MSG.assumableHead)}
                  </Heading>
                  <AssumablesLearnMoreModal
                    buttonProps={{
                      variant: 'ghost',
                      colorScheme: 'primary',
                      leftIcon: <Icon mr={-1} name='question-circle' />,
                      size: 'xs',
                      copy: intl.formatMessage(MSG.info),
                      tracking: {
                        guid: 'wooip54kn9VN5jVu13Vena',
                        ui_context: 'Listing Details',
                        descriptor: 'Info button to learn more about assumables'
                      }
                    }}
                  />
                </Flex>

                <Text size='xs' mb={0}>
                  {intl.formatMessage(MSG.assumableInfo, {
                    type: listing?.assumableLoanType,
                    loanAmount: formatCurrency(assumableMortgageLoanInfo?.loanAmount),
                    termYears: assumableMortgageLoanInfo?.termYears
                  })}
                  <Text as='span' size='xs' color='success.600' fontWeight='bold'>
                    {` ${assumableMortgageLoanInfo?.interestRatePercent}%`}
                  </Text>
                </Text>
                {assumablePotentialSavings !== null && (
                  <Flex direction='row' align='center' gap={1}>
                    {assumablePotentialSavings > 0 ? (
                      <>
                        <Text size='xs' mb={0}>
                          {intl.formatMessage(MSG.possibleSavings)}
                        </Text>
                        <Text size='sm' mb={0} fontWeight='semibold' color='success.700'>
                          {intl.formatMessage(MSG.perMonth, {
                            amount: formatAbbrCurrency(assumablePotentialSavings)
                          })}
                        </Text>
                      </>
                    ) : (
                      <Text mb={0} size='sm' fontWeight='semibold' color='warning.800'>
                        {intl.formatMessage(MSG.likelyNoSavings)}
                      </Text>
                    )}
                    <Text size='xs' mb={0}>
                      ·
                    </Text>
                    <Button variant='link' size='xs' color='primary.600' onClick={handleAssumableDetailsClick}>
                      {intl.formatMessage(MSG.seeDetails)}
                    </Button>
                  </Flex>
                )}
              </Flex>
            </Flex>
          </>
        )}
      </CardBody>
    </Card>
  )
}
